// src/components/themes/default/admin/BranchSidebar.tsx
"use client";

import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  ShoppingBag,
  Store,
  ExternalLink,
  LogOut,
  Images,
  Users,
  X,
  Megaphone,
  Settings,
  MessageSquare,
  Star,
  Boxes,
  ChevronDown,
  ScanBarcode,
  UserCheck,
  Sun,
  Moon,
  BarChart3,
  ShieldAlert,
  Ticket,
  Building2,
  Layers,
  CalendarCheck,
} from "lucide-react";

interface BranchSidebarProps {
  branchSlug: string;
  branchName?: string;
  subdomain: string;
  activeTab: string;
  setActiveTab: (tab: any) => void;
  pendingOrders?: number;
  productCount?: number;
  categoryCount?: number;
  customerCount?: number;
  employeeCount?: number;
  reviewCount?: number;
  onLogout: () => void;
  isMobileOpen?: boolean;
  onCloseMobile?: () => void;
  theme?: "dark" | "light";
  onToggleTheme?: () => void;
  primaryColor?: string;
}

export default function BranchSidebar({
  branchSlug,
  branchName,
  subdomain,
  activeTab,
  setActiveTab,
  pendingOrders = 0,
  productCount = 0,
  categoryCount = 0,
  customerCount = 0,
  employeeCount = 0,
  reviewCount = 0,
  onLogout,
  isMobileOpen = false,
  onCloseMobile,
  theme = "dark",
  onToggleTheme,
  primaryColor = "#059669",
}: BranchSidebarProps) {
  const isLight = theme === "light";
  const [openSection, setOpenSection] = useState<string | null>("catalog");

  const toggleSection = (section: string) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  // 🌟 Active Tab onujayi shothik dropdown section auto-open kora
  useEffect(() => {
    if (
      activeTab.startsWith("analytics") ||
      [
        "customer_intelligence",
        "orders_cashflow",
        "product_curves",
        "product_ai_curves",
        "category_analytics",
      ].includes(activeTab)
    ) {
      setOpenSection("analytics");
    } else if (
      [
        "messages",
        "chat",
        "live_chat",
        "branch_chat",
        "chat_customer",
        "chat_branch_merchant",
        "chat_branch_customer",
      ].includes(activeTab)
    ) {
      setOpenSection("chat");
    } else if (
      [
        "barcode_scanner",
        "barcode",
        "barcode_stock_out",
        "barcode-stock-out",
        "pos_counter",
        "daily_collection",
        "dailyCollection",
        "inventory",
        "inventory_stock",
      ].includes(activeTab)
    ) {
      setOpenSection("pos");
    } else if (
      ["products", "categories", "reviews", "gallery", "product_images", "images"].includes(activeTab)
    ) {
      setOpenSection("catalog");
    } else if (
      ["orders", "fraud_detection", "fraud", "cod_fraud", "customers"].includes(activeTab)
    ) {
      setOpenSection("sales");
    } else if (
      ["employees", "attendance", "staff", "daily_attendance", "payroll", "departments"].includes(activeTab)
    ) {
      setOpenSection("hrm");
    } else if (["coupons", "banners", "newsletter"].includes(activeTab)) {
      setOpenSection("marketing");
    } else if (["settings", "branch_profile", "general_settings"].includes(activeTab)) {
      setOpenSection("settings");
    }
  }, [activeTab]);

  const handleTabClick = (tab: any) => {
    setActiveTab(tab);
    if (onCloseMobile) onCloseMobile();
  };

  // 🌟 Bulletproof Tab Active Checker (Sob alias support shoho)
  const isTabActive = (tabKey: string) => {
    if (activeTab === tabKey) return true;

    // Chat
    if (
      tabKey === "chat_branch_customer" &&
      ["chat_branch_customer", "messages", "chat", "live_chat", "branch_chat"].includes(activeTab)
    ) {
      return true;
    }
    if (tabKey === "chat_branch_merchant" && activeTab === "chat_branch_merchant") return true;
    if (tabKey === "chat_customer" && activeTab === "chat_customer") return true;

    // Analytics
    if (
      tabKey === "analytics_customer" &&
      ["customer_intelligence", "analytics_customer"].includes(activeTab)
    ) {
      return true;
    }
    if (
      tabKey === "analytics_orders" &&
      ["orders_cashflow", "analytics_orders"].includes(activeTab)
    ) {
      return true;
    }
    if (
      tabKey === "analytics_products" &&
      ["product_curves", "product_ai_curves", "analytics_products"].includes(activeTab)
    ) {
      return true;
    }
    if (
      tabKey === "analytics_categories" &&
      ["category_analytics", "analytics_categories"].includes(activeTab)
    ) {
      return true;
    }

    // POS
    if (
      tabKey === "barcode_scanner" &&
      ["barcode_scanner", "barcode_stock_out", "barcode", "barcode-stock-out", "pos_counter"].includes(activeTab)
    ) {
      return true;
    }
    if (
      tabKey === "daily_collection" &&
      ["daily_collection", "daily-collection", "dailyCollection", "collection"].includes(activeTab)
    ) {
      return true;
    }
    if (
      tabKey === "inventory" &&
      ["inventory", "inventory_stock", "inventory-stock", "stock", "warehouse"].includes(activeTab)
    ) {
      return true;
    }

    // Products & Catalog
    if (tabKey === "products" && ["products", "branch_products"].includes(activeTab)) return true;
    if (tabKey === "categories" && ["categories", "branch_categories"].includes(activeTab)) return true;
    if (tabKey === "reviews" && ["reviews", "product_reviews"].includes(activeTab)) return true;
    if (
      tabKey === "gallery" &&
      ["gallery", "product_images", "images"].includes(activeTab)
    ) {
      return true;
    }

    // Sales & Orders
    if (tabKey === "orders" && ["orders", "branch_orders"].includes(activeTab)) return true;
    if (tabKey === "customers" && ["customers", "branch_customers"].includes(activeTab)) return true;
    if (
      tabKey === "fraud_detection" &&
      ["fraud_detection", "fraud", "cod_fraud"].includes(activeTab)
    ) {
      return true;
    }

    // HR & Staff
    if (tabKey === "employees" && ["employees", "branch_employees", "staff"].includes(activeTab)) return true;
    if (
      tabKey === "attendance" &&
      ["attendance", "daily_attendance", "attendance_daily"].includes(activeTab)
    ) {
      return true;
    }

    // Settings
    if (
      tabKey === "settings" &&
      ["settings", "general_settings", "branch_settings", "branch_profile"].includes(activeTab)
    ) {
      return true;
    }

    return false;
  };

  // 🌟 Active Tab Button-er Style
  const getActiveTabStyle = (tabKey: string) => {
    if (isTabActive(tabKey)) {
      return {
        backgroundColor: primaryColor,
        color: "#ffffff",
        boxShadow: `0 6px 16px -2px ${primaryColor}55`,
      };
    }
    return {};
  };

  // 🌟 Dynamic Class Resolver (Light/Dark mode & Active Text styling fix)
  const getSubItemClass = (tabKey: string) => {
    const active = isTabActive(tabKey);
    if (active) {
      return "text-white font-black shadow-xs";
    }
    return isLight
      ? "text-slate-700 hover:text-slate-950 hover:bg-slate-100 font-bold"
      : "text-slate-400 hover:text-white hover:bg-slate-800/50 font-bold";
  };

  const liveStoreUrl = `/b/${branchSlug}?view=store`;

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {isMobileOpen && (
        <div
          onClick={onCloseMobile}
          className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-40 lg:hidden transition-opacity"
        />
      )}

      <aside
        className={`fixed lg:sticky top-0 inset-y-0 left-0 z-50 w-72 lg:w-76 h-screen flex flex-col justify-between p-4 transition-colors duration-200 shrink-0 ${
          isLight
            ? "bg-white border-r-2 border-slate-300 text-slate-800 shadow-sm"
            : "bg-slate-900 border-r border-slate-800 text-white"
        } ${isMobileOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full lg:translate-x-0"}`}
      >
        {/* 🌟 1. Header Identity */}
        <div
          className={`flex items-center justify-between pb-3.5 px-1 shrink-0 border-b-2 ${
            isLight ? "border-slate-200" : "border-slate-800/80"
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              style={{
                backgroundColor: primaryColor,
                boxShadow: `0 4px 14px 0 ${primaryColor}45`,
              }}
              className="w-9 h-9 rounded-2xl flex items-center justify-center font-black text-white shrink-0"
            >
              <Store className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                <h2
                  style={{ color: primaryColor }}
                  className="text-[9px] font-black uppercase tracking-widest"
                >
                  Branch Live
                </h2>
              </div>
              <h1
                className={`text-xs font-black truncate ${
                  isLight ? "text-slate-950" : "text-white"
                }`}
                title={branchName || branchSlug}
              >
                {branchName || branchSlug}
              </h1>
            </div>
          </div>

          <button
            onClick={onCloseMobile}
            type="button"
            className={`lg:hidden p-1.5 rounded-lg transition-colors cursor-pointer ${
              isLight ? "text-slate-400 hover:bg-slate-100" : "text-slate-400 hover:bg-slate-800"
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* 🌟 2. Navigation Menu List */}
        <nav className="flex-1 overflow-y-auto my-2.5 pr-1 space-y-1 text-xs font-bold [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {/* Overview */}
          <button
            onClick={() => handleTabClick("overview")}
            style={getActiveTabStyle("overview")}
            className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-2xl transition-all cursor-pointer ${
              isTabActive("overview")
                ? "text-white font-black shadow-xs"
                : isLight
                ? "text-slate-700 hover:text-slate-950 hover:bg-slate-100"
                : "text-slate-400 hover:text-white hover:bg-slate-800/50"
            }`}
          >
            <LayoutDashboard className="w-4 h-4" />
            <span>Overview</span>
          </button>

          {/* Live Chat Section */}
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => toggleSection("chat")}
              style={
                openSection === "chat"
                  ? {
                      backgroundColor: `${primaryColor}14`,
                      color: isLight ? "#0f172a" : "#f8fafc",
                      borderColor: `${primaryColor}35`,
                    }
                  : {}
              }
              className={`w-full flex items-center justify-between px-3.5 py-2 rounded-xl transition-all cursor-pointer border border-transparent ${
                openSection === "chat"
                  ? ""
                  : isLight
                  ? "text-slate-700 hover:bg-slate-100"
                  : "text-slate-400 hover:bg-slate-800/40"
              }`}
            >
              <div className="flex items-center gap-2">
                <MessageSquare style={{ color: primaryColor }} className="w-4 h-4" />
                <span className="text-[11px] font-black uppercase tracking-wider">Live Chat</span>
              </div>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  openSection === "chat" ? "rotate-180" : "text-slate-400"
                }`}
              />
            </button>

            {openSection === "chat" && (
              <div style={{ borderColor: `${primaryColor}40` }} className="pl-3 py-1 space-y-0.5 border-l-2 ml-4 mt-0.5">
                <button
                  onClick={() => handleTabClick("chat_branch_customer")}
                  style={getActiveTabStyle("chat_branch_customer")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "chat_branch_customer"
                  )}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Store className="w-3.5 h-3.5" />
                    <span>Branch & Customer</span>
                  </div>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                </button>

                <button
                  onClick={() => handleTabClick("chat_branch_merchant")}
                  style={getActiveTabStyle("chat_branch_merchant")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "chat_branch_merchant"
                  )}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" />
                    <span>HQ Merchant Chat</span>
                  </div>
                  <span className="px-1.5 py-0.2 text-[8px] font-black uppercase font-mono rounded bg-blue-500/10 text-blue-400">
                    B2B
                  </span>
                </button>

                <button
                  onClick={() => handleTabClick("chat_customer")}
                  style={getActiveTabStyle("chat_customer")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "chat_customer"
                  )}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" />
                    <span>All Customers</span>
                  </div>
                  <span className="px-1.5 py-0.2 text-[8px] font-black uppercase font-mono rounded bg-slate-800 text-slate-300">
                    Feed
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Analytics & BI */}
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => toggleSection("analytics")}
              style={
                openSection === "analytics"
                  ? {
                      backgroundColor: `${primaryColor}14`,
                      color: isLight ? "#0f172a" : "#f8fafc",
                      borderColor: `${primaryColor}35`,
                    }
                  : {}
              }
              className={`w-full flex items-center justify-between px-3.5 py-2 rounded-xl transition-all cursor-pointer border border-transparent ${
                openSection === "analytics"
                  ? ""
                  : isLight
                  ? "text-slate-700 hover:bg-slate-100"
                  : "text-slate-400 hover:bg-slate-800/40"
              }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 style={{ color: primaryColor }} className="w-4 h-4" />
                <span className="text-[11px] font-black uppercase tracking-wider">Analytics & BI</span>
              </div>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  openSection === "analytics" ? "rotate-180" : "text-slate-400"
                }`}
              />
            </button>

            {openSection === "analytics" && (
              <div style={{ borderColor: `${primaryColor}40` }} className="pl-3 py-1 space-y-0.5 border-l-2 ml-4 mt-0.5">
                <button
                  onClick={() => handleTabClick("analytics_customer")}
                  style={getActiveTabStyle("analytics_customer")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "analytics_customer"
                  )}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" />
                    <span>Customer Intelligence</span>
                  </div>
                </button>

                <button
                  onClick={() => handleTabClick("analytics_orders")}
                  style={getActiveTabStyle("analytics_orders")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "analytics_orders"
                  )}`}
                >
                  <div className="flex items-center gap-1.5">
                    <ShoppingBag className="w-3.5 h-3.5" />
                    <span>Orders & Cashflow</span>
                  </div>
                </button>

                <button
                  onClick={() => handleTabClick("analytics_products")}
                  style={getActiveTabStyle("analytics_products")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "analytics_products"
                  )}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Boxes className="w-3.5 h-3.5" />
                    <span>Product & AI Curves</span>
                  </div>
                </button>

                <button
                  onClick={() => handleTabClick("analytics_categories")}
                  style={getActiveTabStyle("analytics_categories")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "analytics_categories"
                  )}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5" />
                    <span>Category Taxonomies</span>
                  </div>
                </button>
              </div>
            )}
          </div>

          {/* POS & Counter */}
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => toggleSection("pos")}
              style={
                openSection === "pos"
                  ? {
                      backgroundColor: `${primaryColor}14`,
                      color: isLight ? "#0f172a" : "#f8fafc",
                      borderColor: `${primaryColor}35`,
                    }
                  : {}
              }
              className={`w-full flex items-center justify-between px-3.5 py-2 rounded-xl transition-all cursor-pointer border border-transparent ${
                openSection === "pos"
                  ? ""
                  : isLight
                  ? "text-slate-700 hover:bg-slate-100"
                  : "text-slate-400 hover:bg-slate-800/40"
              }`}
            >
              <div className="flex items-center gap-2">
                <ScanBarcode style={{ color: primaryColor }} className="w-4 h-4" />
                <span className="text-[11px] font-black uppercase tracking-wider">POS & Counter</span>
              </div>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  openSection === "pos" ? "rotate-180" : "text-slate-400"
                }`}
              />
            </button>

            {openSection === "pos" && (
              <div style={{ borderColor: `${primaryColor}40` }} className="pl-3 py-1 space-y-0.5 border-l-2 ml-4 mt-0.5">
                <button
                  onClick={() => handleTabClick("barcode_scanner")}
                  style={getActiveTabStyle("barcode_scanner")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "barcode_scanner"
                  )}`}
                >
                  <span>Barcode Stock Out</span>
                  <span className="px-1 py-0.2 text-[8px] font-black uppercase rounded bg-slate-800 text-slate-300">
                    Scan
                  </span>
                </button>
                <button
                  onClick={() => handleTabClick("daily_collection")}
                  style={getActiveTabStyle("daily_collection")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "daily_collection"
                  )}`}
                >
                  <span>Daily Collection</span>
                  <span className="px-1 py-0.2 text-[8px] font-black rounded bg-slate-800 text-slate-300">
                    Audit
                  </span>
                </button>
                <button
                  onClick={() => handleTabClick("inventory")}
                  style={getActiveTabStyle("inventory")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "inventory"
                  )}`}
                >
                  <span>Inventory & Stock</span>
                  <span className="px-1 py-0.2 text-[8px] font-black rounded bg-slate-800 text-slate-300">
                    Live
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Products & Catalog */}
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => toggleSection("catalog")}
              style={
                openSection === "catalog"
                  ? {
                      backgroundColor: `${primaryColor}14`,
                      color: isLight ? "#0f172a" : "#f8fafc",
                      borderColor: `${primaryColor}35`,
                    }
                  : {}
              }
              className={`w-full flex items-center justify-between px-3.5 py-2 rounded-xl transition-all cursor-pointer border border-transparent ${
                openSection === "catalog"
                  ? ""
                  : isLight
                  ? "text-slate-700 hover:bg-slate-100"
                  : "text-slate-400 hover:bg-slate-800/40"
              }`}
            >
              <div className="flex items-center gap-2">
                <Boxes style={{ color: primaryColor }} className="w-4 h-4" />
                <span className="text-[11px] font-black uppercase tracking-wider">Products & Catalog</span>
              </div>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  openSection === "catalog" ? "rotate-180" : "text-slate-400"
                }`}
              />
            </button>

            {openSection === "catalog" && (
              <div style={{ borderColor: `${primaryColor}40` }} className="pl-3 py-1 space-y-0.5 border-l-2 ml-4 mt-0.5">
                <button
                  onClick={() => handleTabClick("products")}
                  style={getActiveTabStyle("products")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "products"
                  )}`}
                >
                  <span>Branch Products</span>
                  <span className="px-1.5 py-0.2 text-[9px] font-mono rounded bg-slate-800 text-slate-300">
                    {productCount}
                  </span>
                </button>

                <button
                  onClick={() => handleTabClick("categories")}
                  style={getActiveTabStyle("categories")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "categories"
                  )}`}
                >
                  <span>Categories</span>
                  <span className="px-1.5 py-0.2 text-[9px] font-mono rounded bg-slate-800 text-slate-300">
                    {categoryCount}
                  </span>
                </button>

                <button
                  onClick={() => handleTabClick("reviews")}
                  style={getActiveTabStyle("reviews")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "reviews"
                  )}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                    <span>Reviews</span>
                  </div>
                  {reviewCount > 0 && (
                    <span className="px-1.5 py-0.2 text-[9px] font-mono rounded bg-slate-800 text-amber-400">
                      {reviewCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => handleTabClick("gallery")}
                  style={getActiveTabStyle("gallery")}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "gallery"
                  )}`}
                >
                  <Images className="w-3.5 h-3.5" />
                  <span>Product Images</span>
                </button>
              </div>
            )}
          </div>

          {/* Sales & Orders */}
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => toggleSection("sales")}
              style={
                openSection === "sales"
                  ? {
                      backgroundColor: `${primaryColor}14`,
                      color: isLight ? "#0f172a" : "#f8fafc",
                      borderColor: `${primaryColor}35`,
                    }
                  : {}
              }
              className={`w-full flex items-center justify-between px-3.5 py-2 rounded-xl transition-all cursor-pointer border border-transparent ${
                openSection === "sales"
                  ? ""
                  : isLight
                  ? "text-slate-700 hover:bg-slate-100"
                  : "text-slate-400 hover:bg-slate-800/40"
              }`}
            >
              <div className="flex items-center gap-2">
                <ShoppingBag style={{ color: primaryColor }} className="w-4 h-4" />
                <span className="text-[11px] font-black uppercase tracking-wider">Sales & Orders</span>
              </div>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  openSection === "sales" ? "rotate-180" : "text-slate-400"
                }`}
              />
            </button>

            {openSection === "sales" && (
              <div style={{ borderColor: `${primaryColor}40` }} className="pl-3 py-1 space-y-0.5 border-l-2 ml-4 mt-0.5">
                <button
                  onClick={() => handleTabClick("orders")}
                  style={getActiveTabStyle("orders")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "orders"
                  )}`}
                >
                  <span>Orders</span>
                  {pendingOrders > 0 ? (
                    <span className="px-1.5 py-0.2 rounded-full bg-amber-500 text-white text-[8px] font-black animate-pulse">
                      {pendingOrders}
                    </span>
                  ) : (
                    <span className="text-[9px] text-slate-400 font-mono">0</span>
                  )}
                </button>

                <button
                  onClick={() => handleTabClick("customers")}
                  style={getActiveTabStyle("customers")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "customers"
                  )}`}
                >
                  <span>Customers</span>
                  <span className="px-1.5 py-0.2 text-[9px] font-mono rounded bg-slate-800 text-slate-300">
                    {customerCount}
                  </span>
                </button>

                <button
                  onClick={() => handleTabClick("fraud_detection")}
                  style={getActiveTabStyle("fraud_detection")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "fraud_detection"
                  )}`}
                >
                  <div className="flex items-center gap-1.5">
                    <ShieldAlert className="w-3.5 h-3.5 text-rose-500" />
                    <span>COD Fraud Shield</span>
                  </div>
                  <span className="px-1 py-0.2 text-[8px] font-black rounded uppercase bg-rose-500/20 text-rose-500">
                    AI
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* HR & Staff */}
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => toggleSection("hrm")}
              style={
                openSection === "hrm"
                  ? {
                      backgroundColor: `${primaryColor}14`,
                      color: isLight ? "#0f172a" : "#f8fafc",
                      borderColor: `${primaryColor}35`,
                    }
                  : {}
              }
              className={`w-full flex items-center justify-between px-3.5 py-2 rounded-xl transition-all cursor-pointer border border-transparent ${
                openSection === "hrm"
                  ? ""
                  : isLight
                  ? "text-slate-700 hover:bg-slate-100"
                  : "text-slate-400 hover:bg-slate-800/40"
              }`}
            >
              <div className="flex items-center gap-2">
                <UserCheck style={{ color: primaryColor }} className="w-4 h-4" />
                <span className="text-[11px] font-black uppercase tracking-wider">HR & Staff</span>
              </div>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  openSection === "hrm" ? "rotate-180" : "text-slate-400"
                }`}
              />
            </button>

            {openSection === "hrm" && (
              <div style={{ borderColor: `${primaryColor}40` }} className="pl-3 py-1 space-y-0.5 border-l-2 ml-4 mt-0.5">
                <button
                  onClick={() => handleTabClick("employees")}
                  style={getActiveTabStyle("employees")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "employees"
                  )}`}
                >
                  <span>Branch Employees</span>
                  {employeeCount > 0 && (
                    <span className="px-1.5 py-0.2 text-[9px] font-mono rounded bg-slate-800 text-slate-300">
                      {employeeCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => handleTabClick("attendance")}
                  style={getActiveTabStyle("attendance")}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "attendance"
                  )}`}
                >
                  <div className="flex items-center gap-1.5">
                    <CalendarCheck className="w-3.5 h-3.5 text-emerald-500" />
                    <span>Attendance Daily</span>
                  </div>
                  <span className="px-1 py-0.2 text-[8px] font-black rounded bg-slate-800 text-slate-300">
                    Live
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Marketing & Promos */}
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => toggleSection("marketing")}
              style={
                openSection === "marketing"
                  ? {
                      backgroundColor: `${primaryColor}14`,
                      color: isLight ? "#0f172a" : "#f8fafc",
                      borderColor: `${primaryColor}35`,
                    }
                  : {}
              }
              className={`w-full flex items-center justify-between px-3.5 py-2 rounded-xl transition-all cursor-pointer border border-transparent ${
                openSection === "marketing"
                  ? ""
                  : isLight
                  ? "text-slate-700 hover:bg-slate-100"
                  : "text-slate-400 hover:bg-slate-800/40"
              }`}
            >
              <div className="flex items-center gap-2">
                <Megaphone style={{ color: primaryColor }} className="w-4 h-4" />
                <span className="text-[11px] font-black uppercase tracking-wider">Marketing & Promos</span>
              </div>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  openSection === "marketing" ? "rotate-180" : "text-slate-400"
                }`}
              />
            </button>

            {openSection === "marketing" && (
              <div style={{ borderColor: `${primaryColor}40` }} className="pl-3 py-1 space-y-0.5 border-l-2 ml-4 mt-0.5">
                <button
                  onClick={() => handleTabClick("coupons")}
                  style={getActiveTabStyle("coupons")}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "coupons"
                  )}`}
                >
                  <Ticket className="w-3.5 h-3.5" />
                  <span>Coupons</span>
                </button>
                <button
                  onClick={() => handleTabClick("banners")}
                  style={getActiveTabStyle("banners")}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "banners"
                  )}`}
                >
                  <Megaphone className="w-3.5 h-3.5" />
                  <span>Banners & Offers</span>
                </button>
              </div>
            )}
          </div>

          {/* Branch Settings */}
          <div className="pt-0.5">
            <button
              type="button"
              onClick={() => toggleSection("settings")}
              style={
                openSection === "settings"
                  ? {
                      backgroundColor: `${primaryColor}14`,
                      color: isLight ? "#0f172a" : "#f8fafc",
                      borderColor: `${primaryColor}35`,
                    }
                  : {}
              }
              className={`w-full flex items-center justify-between px-3.5 py-2 rounded-xl transition-all cursor-pointer border border-transparent ${
                openSection === "settings"
                  ? ""
                  : isLight
                  ? "text-slate-700 hover:bg-slate-100"
                  : "text-slate-400 hover:bg-slate-800/40"
              }`}
            >
              <div className="flex items-center gap-2">
                <Settings style={{ color: primaryColor }} className="w-4 h-4" />
                <span className="text-[11px] font-black uppercase tracking-wider">Branch Settings</span>
              </div>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  openSection === "settings" ? "rotate-180" : "text-slate-400"
                }`}
              />
            </button>

            {openSection === "settings" && (
              <div style={{ borderColor: `${primaryColor}40` }} className="pl-3 py-1 space-y-0.5 border-l-2 ml-4 mt-0.5">
                <button
                  onClick={() => handleTabClick("settings")}
                  style={getActiveTabStyle("settings")}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-xl transition-all cursor-pointer ${getSubItemClass(
                    "settings"
                  )}`}
                >
                  <Settings className="w-3.5 h-3.5" />
                  <span>General Settings</span>
                </button>
              </div>
            )}
          </div>
        </nav>

        {/* 🌟 3. Footer Control Bar */}
        <div
          className={`space-y-2.5 pt-2.5 border-t-2 shrink-0 ${
            isLight ? "border-slate-200" : "border-slate-800/80"
          }`}
        >
          {/* Light/Dark Mode Switch */}
          <div
            className={`p-2.5 rounded-2xl flex items-center justify-between text-xs font-bold transition-all border ${
              isLight ? "bg-slate-50 border-slate-300 shadow-2xs" : "bg-slate-950/60 border-slate-800"
            }`}
          >
            <div className="flex items-center gap-2">
              {isLight ? <Sun className="w-4 h-4 text-amber-500" /> : <Moon className="w-4 h-4 text-slate-400" />}
              <span className={isLight ? "text-slate-900 font-black" : "text-slate-300"}>
                {isLight ? "Light Mode" : "Dark Mode"}
              </span>
            </div>

            <button
              type="button"
              onClick={onToggleTheme}
              style={isLight ? { backgroundColor: primaryColor } : {}}
              className={`w-11 h-6 rounded-full p-0.5 transition-colors cursor-pointer relative ${
                isLight ? "" : "bg-slate-700"
              }`}
            >
              <div
                className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform ${
                  isLight ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          {/* Storefront & Log Out Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <a
              href={liveStoreUrl}
              target="_blank"
              rel="noreferrer"
              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all border-2 cursor-pointer ${
                isLight
                  ? "text-slate-800 bg-white hover:bg-slate-100 border-slate-300"
                  : "text-slate-300 bg-slate-800/80 hover:bg-slate-800 hover:text-white border-slate-700/60"
              }`}
              title="View Public Storefront"
            >
              <ExternalLink style={{ color: primaryColor }} className="w-3.5 h-3.5" />
              <span>Store</span>
            </a>

            <button
              onClick={onLogout}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-rose-500 bg-rose-500/10 hover:bg-rose-500/20 border-2 border-rose-500/30 transition-all cursor-pointer"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Log Out</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}